import { useQuery } from '@tanstack/react-query';
import { Building2, FileText, BarChart3 } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { assetsApi } from '@/lib/api';
import { useAuth } from '@/store/auth';
import TemplateEditor from './TemplateEditor';

export default function FacilityAssetsTab() {
  const { user } = useAuth();

  // Get facility ID from logged-in user
  const facilityId = user?.facilityId || '';

  // Fetch assets for the facility (for stats only)
  const { data: assetsData } = useQuery({
    queryKey: ['facility-assets', facilityId],
    queryFn: () => assetsApi.list({ facilityId, limit: 100 }),
    enabled: !!facilityId,
  });

  const assets = assetsData?.assets || [];

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const totalFileSize = assets.reduce((sum, asset) => sum + asset.fileSize, 0);

  if (!facilityId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-red-600">Facility ID not found. Please contact administrator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Facility Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Facility
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{facilityId}</p>
            <p className="text-sm text-muted-foreground">Your facility</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-green-600" />
              Assets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{assets.length}</p>
            <p className="text-sm text-muted-foreground">Total assets</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-purple-600" />
              Storage Used
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatFileSize(totalFileSize)}</p>
            <p className="text-sm text-muted-foreground">Total storage</p>
          </CardContent>
        </Card>
      </div>

      {/* Template Editor */}
      <Card className="h-[calc(100vh-280px)]">
        <CardContent className="p-0 h-full">
          <TemplateEditor facilityId={facilityId} isAdmin={false} />
        </CardContent>
      </Card>
    </div>
  );
}
